import React, { Fragment, useCallback, useEffect, useReducer } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";

import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";

import PageHeaderRowSearch from "../components/shared/page-header-row-search";
import { MetricsSalesFilters } from "../components/metrics-sales/metrics-sales-filters";
import { MetricsSalesTable } from "../components/metrics-sales/metrics-sales-table";
import ai from "../helpers/axios";
import currencyFormatter from "../helpers/currencyFormatter";

const tableDefaultState = {
  rows: [],
  totalItems: 0,
  totals: null,
  countPerPage: 10,
  currentPage: 1,
};

const initialState = {
  input: { dates: [], owners: [] },
  applied: { dates: [], owners: [] },
  signed: {
    ...tableDefaultState,
    sortBy: "signedContracts",
    sortDirection: "desc",
  },
  splits: {
    ...tableDefaultState,
    sortBy: "totalSignedContracts",
    sortDirection: "desc",
  },
  loading: { signed: false, splits: false },
};

const currencyCell = (field) => (row) => {
  const value = row?.[field];
  if ((value || value === 0) && !isNaN(value)) {
    return currencyFormatter.format(value);
  }
  return "";
};

const signedColumns = [
  { name: "Rep", selector: "rep", minWidth: "180px", sortable: true },
  {
    name: "Signed Contracts",
    selector: "signedContracts",
    right: true,
    minWidth: "120px",
    sortable: true,
  },
  {
    name: "Total Charges",
    selector: "totalCharges",
    right: true,
    minWidth: "120px",
    cell: currencyCell("totalCharges"),
    sortable: true,
  },
];

const splitsColumns = [
  { name: "Rep", selector: "rep", minWidth: "180px", sortable: true },
  {
    name: "iOS #",
    selector: "iosCount",
    right: true,
    minWidth: "80px",
    sortable: true,
  },
  {
    name: "iOS $",
    selector: "iosCharges",
    right: true,
    minWidth: "110px",
    cell: currencyCell("iosCharges"),
    sortable: true,
  },
  {
    name: "Standard #",
    selector: "standardCount",
    right: true,
    minWidth: "90px",
    sortable: true,
  },
  {
    name: "Standard $",
    selector: "standardCharges",
    right: true,
    minWidth: "110px",
    cell: currencyCell("standardCharges"),
    sortable: true,
  },
];

const reducer = (state, action) => {
  switch (action.type) {
    case "inputChanged":
      return { ...state, input: { ...state.input, ...action.patch } };

    case "applyFilters":
      return {
        ...state,
        applied: state.input,
        signed: { ...state.signed, currentPage: 1 },
        splits: { ...state.splits, currentPage: 1 },
      };

    case "loading":
      return {
        ...state,
        loading: { ...state.loading, [action.table]: action.value },
      };

    case "dataLoaded":
      return {
        ...state,
        loading: { ...state.loading, [action.table]: false },
        [action.table]: {
          ...state[action.table],
          rows: action.data.data,
          totalItems: action.data.totalLength,
          totals: action.data.totals,
        },
      };

    case "pageChanged":
      return {
        ...state,
        [action.table]: { ...state[action.table], currentPage: action.page },
      };

    case "rowsPerPageChanged":
      return {
        ...state,
        [action.table]: {
          ...state[action.table],
          countPerPage: action.countPerPage,
          currentPage: 1,
        },
      };

    case "sortingChanged":
      return {
        ...state,
        [action.table]: {
          ...state[action.table],
          sortBy: action.sortBy,
          sortDirection: action.sortDirection,
          currentPage: 1,
        },
      };

    default:
      return state;
  }
};

const buildTablePaging = (table) => ({
  sortBy: table.sortBy,
  sortDirection: table.sortDirection,
  currentPage: table.currentPage,
  countPerPage: table.countPerPage,
});

const MetricsSales = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    document.title = "Metrics - Sales | evoJets HQ";
  }, []);

  const fetchTable = useCallback(
    async (table, tablePaging, applied) => {
      const token = await getAccessTokenSilently().catch(() => {
        return loginWithRedirect();
      });

      dispatch({ type: "loading", table, value: true });

      const body = {
        table,
        dates: applied.dates,
        owners: {
          ids: (applied.owners ?? []).map((o) => o.value),
          applyDefaultExclusions: true,
        },
        [table]: tablePaging,
      };

      try {
        const response = await ai.auth(token).post(`/api/metrics/sales`, body);

        if (!response?.data?.success) {
          dispatch({ type: "loading", table, value: false });
          toast.error("Error getting sales metrics");
          return;
        }

        dispatch({
          type: "dataLoaded",
          table,
          data: response.data.metrics[table],
        });
      } catch (e) {
        console.error(e);
        dispatch({ type: "loading", table, value: false });
        toast.error("Error getting sales metrics");
      }
    },
    [getAccessTokenSilently, loginWithRedirect]
  );

  const signedPaging = buildTablePaging(state.signed);
  const splitsPaging = buildTablePaging(state.splits);

  useEffect(() => {
    fetchTable("signed", signedPaging, state.applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchTable,
    state.applied,
    state.signed.sortBy,
    state.signed.sortDirection,
    state.signed.currentPage,
    state.signed.countPerPage,
  ]);

  useEffect(() => {
    fetchTable("splits", splitsPaging, state.applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchTable,
    state.applied,
    state.splits.sortBy,
    state.splits.sortDirection,
    state.splits.currentPage,
    state.splits.countPerPage,
  ]);

  return (
    <Fragment>
      <PageHeaderRowSearch h1="Sales"/>

      <MetricsSalesFilters
        dates={state.input.dates}
        owners={state.input.owners}
        isLoading={state.loading.signed || state.loading.splits}
        onChange={(patch) => dispatch({ type: "inputChanged", patch })}
        onApply={() => dispatch({ type: "applyFilters" })}
      />

      <Stack direction="row" gap={5} flexWrap="wrap" mt={3}>
        <Box flex={1}>
          <MetricsSalesTable
            title="Signed Contracts"
            columns={signedColumns}
            rows={state.signed.rows}
            totals={state.signed.totals}
            isLoading={state.loading.signed}
            pagination={{
              totalItems: state.signed.totalItems,
              countPerPage: state.signed.countPerPage,
              currentPage: state.signed.currentPage,
            }}
            sorting={{
              sortBy: state.signed.sortBy,
              sortDirection: state.signed.sortDirection,
            }}
            onPage={(page) => dispatch({ type: "pageChanged", table: "signed", page })}
            onRowsPerPage={(countPerPage) =>
              dispatch({ type: "rowsPerPageChanged", table: "signed", countPerPage })
            }
            onSort={(column, sortDirection) =>
              dispatch({
                type: "sortingChanged",
                table: "signed",
                sortBy: column.selector,
                sortDirection,
              })
            }
          />
        </Box>

        <Box flex={1}>
          <MetricsSalesTable
            title="Contract Splits"
            columns={splitsColumns}
            rows={state.splits.rows}
            totals={state.splits.totals}
            isLoading={state.loading.splits}
            pagination={{
              totalItems: state.splits.totalItems,
              countPerPage: state.splits.countPerPage,
              currentPage: state.splits.currentPage,
            }}
            sorting={{
              sortBy: state.splits.sortBy,
              sortDirection: state.splits.sortDirection,
            }}
            onPage={(page) => dispatch({ type: "pageChanged", table: "splits", page })}
            onRowsPerPage={(countPerPage) =>
              dispatch({ type: "rowsPerPageChanged", table: "splits", countPerPage })
            }
            onSort={(column, sortDirection) =>
              dispatch({
                type: "sortingChanged",
                table: "splits",
                sortBy: column.selector,
                sortDirection,
              })
            }
          />
        </Box>
      </Stack>
    </Fragment>
  );
};

export default MetricsSales;
